// src/pages/ExamListPage/index.js
// 仕様:
// - tests コレクションを新着順で取得し一覧表示
// - 開始/終了時刻に応じてステータスタグ（開催前/開催中/終了）を表示
// - 参加可能なときのみ「受験する」ボタンを有効化
// - UI文言・コメントは日本語

import React, { useState, useEffect, useCallback } from 'react';
import { Typography, Table, Button, App, Tag } from 'antd'; // ← Spin を削除
import { PlayCircleOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { db } from '../../firebase-config';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import dayjs from 'dayjs';

const { Title } = Typography;

const ExamListPage = () => {
  const [exams, setExams] = useState([]);
  const [loading, setLoading] = useState(true);
  const { message } = App.useApp();

  // 試験一覧の取得
  const fetchExams = useCallback(async () => {
    setLoading(true);
    try {
      const testsCollectionRef = collection(db, 'tests');
      // createdAt がない場合はサーバーで付与するか、クライアント側で並べ替えへ変更してください
      const q = query(testsCollectionRef, orderBy('createdAt', 'desc'));
      const snap = await getDocs(q);
      setExams(snap.docs.map((d) => ({ ...d.data(), id: d.id })));
    } catch (error) {
      console.error('試験一覧の取得に失敗:', error);
      message.error('試験一覧を読み込めませんでした。');
    } finally {
      setLoading(false);
    }
  }, [message]);

  useEffect(() => {
    fetchExams();
  }, [fetchExams]);

  // ステータス判定
  const getTestStatus = (test) => {
    const now = dayjs();
    const startTime = test.startTime ? dayjs(test.startTime.toDate()) : null;
    const endTime = test.endTime ? dayjs(test.endTime.toDate()) : null;

    if (startTime && now.isBefore(startTime)) {
      return { text: '開催前', color: 'geekblue', active: false };
    }
    if (endTime && now.isAfter(endTime)) {
      return { text: '終了', color: 'gray', active: false };
    }
    return { text: '開催中', color: 'green', active: true };
  };

  const columns = [
    { title: '試験名', dataIndex: 'testName', key: 'testName' },
    {
      title: 'レベル',
      dataIndex: 'levelId',
      key: 'levelId',
      render: (levelId) => (levelId ? levelId.replace('level', 'Level ') : '-'),
    },
    {
      title: '設問数',
      key: 'qCount',
      align: 'center',
      render: (_, record) => record.questions?.length || 0,
    },
    {
      title: '制限時間',
      dataIndex: 'timeLimit',
      key: 'timeLimit',
      align: 'center',
      render: (time) => (time ? `${time} 分` : '制限なし'),
    },
    {
      title: 'ステータス',
      key: 'status',
      align: 'center',
      render: (_, record) => {
        const status = getTestStatus(record);
        return <Tag color={status.color}>{status.text}</Tag>;
      },
    },
    {
      title: '操作',
      key: 'action',
      align: 'center',
      render: (_, record) => {
        const status = getTestStatus(record);
        return (
          <Link to={`/take-test/${record.id}`}>
            <Button
              type="primary"
              icon={<PlayCircleOutlined />}
              disabled={!status.active}
            >
              受験する
            </Button>
          </Link>
        );
      },
    },
  ];

  return (
    <div>
      <Title level={2}>試験一覧</Title>
      <Table
        columns={columns}
        dataSource={exams.map((t) => ({ ...t, key: t.id }))}
        loading={loading}
        bordered
      />
    </div>
  );
};

export default ExamListPage;
